<template>
  <v-container fluid class="container">
    <v-card class="mb-4">
      <v-card-title class="pb-2">Performance Monitoring</v-card-title>
      <v-card-text>
        <v-row dense align="end">
          <v-col cols="12" sm="6" md="2">
            <v-select
              :items="sourceItems"
              v-model="filters.source"
              label="Source"
              clearable
              dense
              hide-details
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="filters.endpoint"
              label="Endpoint"
              clearable
              dense
              hide-details
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="filters.planning_id"
              label="Planning ID"
              clearable
              dense
              hide-details
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.planning_username"
              label="Planning Username"
              clearable
              dense
              hide-details
              outlined
            />
          </v-col>
        </v-row>
        <v-row dense align="end">
          <v-col cols="12" sm="6" md="3">
            <v-menu
              v-model="fromMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.from"
                  label="From"
                  clearable
                  dense
                  hide-details
                  outlined
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  prepend-inner-icon="mdi-calendar"
                />
              </template>
              <v-date-picker
                v-model="filters.from"
                @input="fromMenu = false"
                no-title
              />
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-menu
              v-model="toMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.to"
                  label="To"
                  clearable
                  dense
                  hide-details
                  outlined
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  prepend-inner-icon="mdi-calendar"
                />
              </template>
              <v-date-picker
                v-model="filters.to"
                @input="toMenu = false"
                no-title
              />
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="3" class="d-flex align-end">
            <v-btn color="primary" class="mr-2" @click="applyFilters" :loading="requestsLoading || summaryLoading">
              Apply filters
            </v-btn>
            <v-btn text @click="resetFilters">Reset</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-alert v-if="errorText" type="error" dense outlined class="mb-4">
      {{ errorText }}
    </v-alert>

    <v-card class="mb-4">
      <v-card-title class="pb-2">Endpoint Summary</v-card-title>
      <v-data-table
        :headers="summaryHeaders"
        :items="summaryRows"
        :loading="summaryLoading"
        class="elevation-0"
        dense
      >
        <template v-slot:item.avg_duration_ms="{ item }">
          {{ formatDuration(item.avg_duration_ms) }}
        </template>
        <template v-slot:item.max_duration_ms="{ item }">
          {{ formatDuration(item.max_duration_ms) }}
        </template>
      </v-data-table>
    </v-card>

    <v-card class="mb-4">
      <v-card-title class="pb-2">Requests</v-card-title>
      <v-data-table
        :headers="requestHeaders"
        :items="requests"
        :server-items-length="totalRequests"
        :loading="requestsLoading"
        :options.sync="requestOptions"
        @update:options="fetchRequests"
        item-key="request_id"
        class="elevation-0"
        dense
      >
        <template v-slot:item.started_at="{ item }">
          {{ formatDateTime(item.started_at) }}
        </template>
        <template v-slot:item.finished_at="{ item }">
          {{ formatDateTime(item.finished_at) }}
        </template>
        <template v-slot:item.duration_ms="{ item }">
          {{ formatDuration(item.duration_ms) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn small text color="primary" @click.stop="selectRequest(item)">
            Steps
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-card>
      <v-card-title class="pb-2">
        Request Steps
        <span v-if="selectedRequest" class="ml-2 text-caption">
          {{ selectedRequest.request_id }}
        </span>
      </v-card-title>
      <v-data-table
        :headers="stepHeaders"
        :items="selectedSteps"
        :loading="stepsLoading"
        class="elevation-0"
        dense
      >
        <template v-slot:item.started_at="{ item }">
          {{ formatDateTime(item.started_at) }}
        </template>
        <template v-slot:item.finished_at="{ item }">
          {{ formatDateTime(item.finished_at) }}
        </template>
        <template v-slot:item.duration_ms="{ item }">
          {{ formatDuration(item.duration_ms) }}
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import api from '../api/lbt-api';

const {
  getPerformanceRequests,
  getPerformanceRequestSteps,
  getPerformanceSummary,
} = api;

const defaultFilters = () => ({
  source: '',
  endpoint: '',
  planning_id: '',
  planning_username: '',
  from: '',
  to: '',
});

export default {
  name: 'PerformanceMetrics',
  data() {
    return {
      filters: defaultFilters(),
      sourceItems: ['pdf', 'excel', 'other'],
      fromMenu: false,
      toMenu: false,
      errorText: '',
      requests: [],
      summaryRows: [],
      selectedSteps: [],
      selectedRequest: null,
      totalRequests: 0,
      requestsLoading: false,
      summaryLoading: false,
      stepsLoading: false,
      requestOptions: {
        page: 1,
        itemsPerPage: 25,
      },
      requestHeaders: [
        { text: 'Started', value: 'started_at' },
        { text: 'Duration (s)', value: 'duration_ms' },
        { text: 'Steps', value: 'step_count' },
        { text: 'Status', value: 'status_code' },
        { text: 'Source', value: 'source' },
        { text: 'Endpoint', value: 'endpoint' },
        { text: 'Path', value: 'path' },
        { text: 'Planning ID', value: 'planning_id' },
        { text: 'Username', value: 'planning_username' },
        { text: 'Worker', value: 'worker_pid' },
        { text: '', value: 'actions', sortable: false },
      ],
      summaryHeaders: [
        { text: 'Endpoint', value: 'endpoint' },
        { text: 'Source', value: 'source' },
        { text: 'Requests', value: 'request_count' },
        { text: 'Avg (s)', value: 'avg_duration_ms' },
        { text: 'Max (s)', value: 'max_duration_ms' },
        { text: 'Success %', value: 'success_rate' },
      ],
      stepHeaders: [
        { text: '#', value: 'step_order' },
        { text: 'Type', value: 'step_type' },
        { text: 'Key', value: 'step_key' },
        { text: 'Label', value: 'step_label' },
        { text: 'Duration (s)', value: 'duration_ms' },
        { text: 'Started', value: 'started_at' },
        { text: 'Finished', value: 'finished_at' },
      ],
    };
  },
  async created() {
    await Promise.all([this.fetchRequests(), this.fetchSummary()]);
  },
  methods: {
    buildQueryParams() {
      const queryParams = {
        source: this.filters.source,
        endpoint: this.filters.endpoint,
        planning_id: this.filters.planning_id,
        planning_username: this.filters.planning_username,
        from: this.filters.from,
        to: this.filters.to,
      };
      return queryParams;
    },
    formatDateTime(value) {
      if (!value) {
        return '';
      }
      return String(value).replace('T', ' ').replace('Z', '');
    },
    formatDuration(value) {
      if (value === null || value === undefined) {
        return '';
      }
      const seconds = Number(value) / 1000;
      if (Number.isNaN(seconds)) {
        return '';
      }
      return `${seconds.toFixed(3)} s`;
    },
    async fetchRequests() {
      this.requestsLoading = true;
      this.errorText = '';
      try {
        const { page, itemsPerPage } = this.requestOptions;
        const sortBy = Array.isArray(this.requestOptions.sortBy) ? this.requestOptions.sortBy[0] : null;
        const sortDesc = Array.isArray(this.requestOptions.sortDesc) ? this.requestOptions.sortDesc[0] : null;
        const response = await getPerformanceRequests({
          page,
          size: itemsPerPage,
          sort_by: sortBy,
          sort_desc: sortDesc,
          ...this.buildQueryParams(),
        });
        this.requests = response.rows || [];
        this.totalRequests = response.total || 0;
        if (this.requests.length && !this.selectedRequest) {
          const withSteps = this.requests.find(row => Number(row.step_count || 0) > 0);
          if (withSteps) {
            this.selectRequest(withSteps);
          }
        }
      } catch (e) {
        this.errorText = e.message || 'Failed to load performance requests';
      } finally {
        this.requestsLoading = false;
      }
    },
    async fetchSummary() {
      this.summaryLoading = true;
      this.errorText = '';
      try {
        const response = await getPerformanceSummary(this.buildQueryParams());
        this.summaryRows = response.rows || [];
      } catch (e) {
        this.errorText = e.message || 'Failed to load performance summary';
      } finally {
        this.summaryLoading = false;
      }
    },
    async selectRequest(item) {
      if (!item || !item.request_id) {
        return;
      }
      this.selectedRequest = item;
      this.stepsLoading = true;
      this.errorText = '';
      try {
        const response = await getPerformanceRequestSteps(item.request_id);
        this.selectedSteps = response.rows || [];
      } catch (e) {
        this.errorText = e.message || 'Failed to load request steps';
        this.selectedSteps = [];
      } finally {
        this.stepsLoading = false;
      }
    },
    async applyFilters() {
      this.requestOptions.page = 1;
      this.selectedRequest = null;
      this.selectedSteps = [];
      await Promise.all([this.fetchRequests(), this.fetchSummary()]);
    },
    async resetFilters() {
      this.filters = defaultFilters();
      await this.applyFilters();
    },
  },
};
</script>
